<template> 
  <Modal :show="true" @close-modal="closeModal" class="team-members-modal" @click.stop>
    <div class="team-members-modal__content">
      <div class="team-members-modal__header">
        <h2 class="team-members-modal__title">{{ role.name }}</h2>
        <span class="team-members-modal__count">
          {{ role.teamMembers.length }} {{ role.teamMembers.length === 1 ? 'Member' : 'Members' }}
        </span>
      </div>

      <div class="team-members-modal__table-wrapper">
        <table class="team-members-modal__table">
          <thead>
            <tr>
              <th class="team-members-modal__th">Name</th>
              <th class="team-members-modal__th">Date Added</th>
              <th class="team-members-modal__th">Email</th>
              <th class="team-members-modal__th team-members-modal__th--action"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(user, index) in role.teamMembers"
              :key="index"
              class="team-members-modal__row"
            >
              <td class="team-members-modal__td team-members-modal__td--name">
                {{ user.fname }} {{ user.lname }}
              </td>
              <td class="team-members-modal__td">
                {{ formatISODate(user.createdAt) }}
              </td>
              <td class="team-members-modal__td">
                {{ user.email }}
              </td>
              <td class="team-members-modal__td team-members-modal__td--action">
                <Popper offsetDistance="5" placement="bottom-end">
                  <button class="team-members-modal__action-trigger">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="3" r="1.5" fill="#64748B"/>
                      <circle cx="8" cy="8" r="1.5" fill="#64748B"/>
                      <circle cx="8" cy="13" r="1.5" fill="#64748B"/>
                    </svg>
                  </button>
                  <template #content>
                    <div class="team-members-modal__dropdown">
                      <button
                        @click.stop="removeUser(user.id, role.id)"
                        class="team-members-modal__dropdown-item team-members-modal__dropdown-item--danger"
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3.5 4.5H10.5L9.83333 12.5H4.16667L3.5 4.5Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M2.5 4.5H11.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
                          <path d="M5.5 4.5V3C5.5 2.44772 5.94772 2 6.5 2H7.5C8.05228 2 8.5 2.44772 8.5 3V4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
                          <path d="M5.5 7V10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
                          <path d="M8.5 7V10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
                        </svg>
                        Remove Member
                      </button>
                    </div>
                  </template>
                </Popper>
              </td>
            </tr>
            <tr v-if="loading">
              <td colspan="4" class="team-members-modal__td team-members-modal__td--loading">
                <div class="team-members-modal__spinner"></div>
              </td>
            </tr>
            <tr v-if="!loading && role.teamMembers.length === 0">
              <td colspan="4" class="team-members-modal__td team-members-modal__td--empty">
                No members assigned to this role
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="team-members-modal__footer">
        <AddTeamMemberButton title="Add Member" :addteam="true" />
      </div>
    </div>
  </Modal>
</template>
  
  <script>
import { ref } from "vue";
import Modal from "@/components/general/Modal";
import { useStore } from "vuex";
import { formatISODate, formatISOTime } from "@/utils/helpers.js";
import { useRouter } from "vue-router";
import AddTeamMemberButton from "@/components/teams/AddTeamMemberButton.vue";
import Popper from "vue3-popper";

export default {
  name: "TeamMembersModal",
  components: {
    Modal,
    AddTeamMemberButton,
    Popper
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    role: {
      type: Object,
      default: () => {},
    },
  },
  emits: ["close-modal", "success"],

  setup(props, { emit }) {
    const router = useRouter();
    const store = useStore();
    const activeUserMenu = ref(null);

    const closeModal = () => {
      emit("close-modal");

      activeUserMenu.value = null;
    };

    const toggleUserActions = (index) => {
      if (activeUserMenu.value === index) {
        activeUserMenu.value = null;
      } else {
        activeUserMenu.value = index;
      }
    };

    async function removeUser(index, roleIndex) {
      try {
        await store.dispatch("organisationModule/deleteTeamMember", {
          memberId: index,
          roleId: roleIndex,
        });
        emit("success");
      } catch (error) {
        console.error("Failed to remove team member:", error);
      }
    }

    const navigateToCreateRole = () => {
      router.push("/dashboard/teams/create-role");
    };

    function formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString();
    }

    return {
      activeUserMenu,
      closeModal,
      toggleUserActions,
      removeUser,
      formatDate,
      formatISODate,
      formatISOTime,
      navigateToCreateRole,
    };
  },
};
</script>

<style lang="scss" scoped>
.team-members-modal {
  &__content {
    width: 100%;
    max-width: 720px;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #F1F5F9;
  }

  &__title {
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0;
  }

  &__count {
    font-size: 12px;
    font-weight: 500;
    color: #64748B;
    background: #F1F5F9;
    padding: 4px 10px;
    border-radius: 12px;
  }

  &__table-wrapper {
    overflow-x: auto;
    max-height: 320px;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
  }

  &__th {
    padding: 12px 16px;
    font-size: 11px;
    font-weight: 500;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: left;
    background: #F8FAFC;
    border-bottom: 1px solid #E2E8F0;
    white-space: nowrap;

    &--action {
      width: 48px;
    }
  }

  &__row {
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #F8FAFC;
    }

    &:not(:last-child) {
      .team-members-modal__td {
        border-bottom: 1px solid #F1F5F9;
      }
    }
  }

  &__td {
    padding: 14px 16px;
    font-size: 14px;
    color: #475569;
    white-space: nowrap;

    &--name {
      font-weight: 500;
      color: #0F172A;
    }

    &--action {
      text-align: center;
      width: 48px;
    }

    &--loading,
    &--empty {
      text-align: center;
      padding: 32px 16px;
      color: #64748B;
    }
  }

  &__action-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #F1F5F9;
    }
  }

  &__dropdown {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    padding: 4px;
    min-width: 160px;
  }

  &__dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 12px;
    font-size: 13px;
    color: #475569;
    background: none;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background-color: #F8FAFC;
    }

    &--danger {
      color: #EF4444;

      &:hover {
        background-color: #FEF2F2;
      }
    }
  }

  &__spinner {
    width: 24px;
    height: 24px;
    margin: 0 auto;
    border: 2px solid #E2E8F0;
    border-top-color: #001240;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  &__footer {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>